var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable import/first */
// Safari lacks the ReadableStream async iteration pdf.js relies on; install the polyfill before
// any PDF is loaded. TODO: remove once https://github.com/mozilla/pdf.js/issues/20973 ships.
import 'shared/domains/documents/utils/readableStreamAsyncIteratorPolyfill';
import { config } from '@fortawesome/fontawesome-svg-core';
// Disable auto CSS injection for CSP compliance.
// Run as soon as possible.
// @see https://docs.fontawesome.com/web/dig-deeper/security/
config.autoAddCss = false;
import { StrictMode } from 'react';
import { i18n } from '@lingui/core';
import { createBrowserHistory, createPath } from 'history';
import { createRoot } from 'react-dom/client';
import { auth } from 'app/domains/auth/client';
import authenticate from 'app/domains/auth/utils/authenticate';
import { isPublicRoute } from 'app/domains/urls';
import { dangerouslySetCurrentLocale } from 'shared/domains/languages/config';
import { detectLocale } from 'shared/domains/languages/utils/detectLocale';
import { dangerouslySetCurrentMarket, getCurrentMarket } from 'shared/domains/markets/config';
import detectMarket from 'shared/domains/markets/utils/detectMarket';
import * as Sentry from 'shared/domains/sentry';
import { Root } from './Root';
const integrations = [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
    }),
    // Disabling for performance optimization on pages with a lot of event listeners and DOM elements
    // Re-evaluate if sentry logs get less useful and we see a real use-case for having them
    Sentry.browserApiErrorsIntegration({
        setTimeout: false,
        setInterval: false,
        requestAnimationFrame: false,
        eventTarget: false,
    }),
];
Sentry.init({
    dsn: process.env.SENTRY_DSN,
    maxBreadcrumbs: 50,
    debug: false,
    application: 'app',
    integrations,
    // Tracing
    tracePropagationTargets: [],
    tracesSampleRate: 0.2,
    // Session Replay
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 0.5,
});
const history = createBrowserHistory();
/**
 * App URLs used to be prefixed with a market segment (e.g. `/de/contracts`). Old links (emails,
 * bookmarks, etc.) may still point at those URLs, so strip the segment before the router mounts
 * and redirect to the market-less equivalent. See `<Redirect from="/:market(se|uk|no|de)/*">` in
 * `app/screens/index.tsx` for the in-router fallback covering anything that reaches the router
 * without going through this bootstrap step.
 */
function stripMarketFromHistory() {
    const market = detectMarket(history.location.pathname);
    if (!market) {
        return;
    }
    const pathname = history.location.pathname.replace(new RegExp(`^/${market.code}(?=/|$)`), '') || '/';
    history.replace(Object.assign(Object.assign({}, history.location), { pathname }));
    dangerouslySetCurrentMarket(market.code);
}
function initLocale(user) {
    return __awaiter(this, void 0, void 0, function* () {
        const { preferredLocale } = user;
        const locale = detectLocale(getCurrentMarket().marketLocales, preferredLocale);
        return dangerouslySetCurrentLocale(locale.code);
    });
}
function render() {
    const container = document.getElementById('app-root');
    const root = createRoot(container);
    root.render(<StrictMode>
      <Root auth={auth} language={i18n.locale}/>
    </StrictMode>);
}
function init() {
    return __awaiter(this, void 0, void 0, function* () {
        // must run before isPublicRoute() (checked below) and before the router mounts
        stripMarketFromHistory();
        let user;
        try {
            user = yield authenticate({ client: auth, history });
        }
        catch (_a) {
            /**
             * Something unexpected went wrong during authentication, render the app anyway.
             * This does not happen if you're just regularly unauthenticated
             * - If the route does not require authentication, it will render normally.
             * - If the route requires authentication, it will display an error page from AuthProvider
             *
             * Don't do loginWithRedirect here, to avoid eternal loop if you're on an outdated auth/callback URL for example
             */
            yield initLocale({ preferredLocale: user === null || user === void 0 ? void 0 : user.preferredLocale });
            return render();
        }
        // prevent initiating & cancelling requests before redirecting to login. prevents unnecessary sentry reports
        if (!user && !isPublicRoute()) {
            return auth.loginWithRedirect({ appState: { returnTo: createPath(window.location) } });
        }
        yield initLocale({ preferredLocale: user === null || user === void 0 ? void 0 : user.preferredLocale });
        return render();
    });
}
init();
